import { useCallback, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Upload } from 'lucide-react';

interface UploadDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  multiple?: boolean;
}

// Browsers set `file.type` inconsistently for .md, .ipynb, and sometimes text
// files. We accept by either MIME type or extension.
const ACCEPTED_MIME = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
  'application/x-ipynb+json',
]);
const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.md', '.markdown', '.txt', '.ipynb'];
const MAX_SIZE = 50 * 1024 * 1024; // 50MB

function hasAcceptedExtension(name: string): boolean {
  const lower = name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export function UploadDropzone({ onFilesSelected, disabled, multiple = true }: UploadDropzoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateAndSelect = useCallback(
    (files: FileList | File[]) => {
      setError(null);
      const list = Array.from(files);
      if (list.length === 0) return;
      const accepted: File[] = [];
      const rejected: string[] = [];
      for (const file of list) {
        const typeOk = ACCEPTED_MIME.has(file.type) || hasAcceptedExtension(file.name);
        if (!typeOk) {
          rejected.push(`${file.name} — unsupported type`);
          continue;
        }
        if (file.size > MAX_SIZE) {
          rejected.push(`${file.name} — over 50MB`);
          continue;
        }
        accepted.push(file);
      }
      if (rejected.length > 0) {
        setError(`Skipped: ${rejected.join('; ')}`);
      }
      if (accepted.length > 0) {
        onFilesSelected(multiple ? accepted : [accepted[0]]);
      }
    },
    [onFilesSelected, multiple],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      if (e.dataTransfer.files) validateAndSelect(e.dataTransfer.files);
    },
    [disabled, validateAndSelect],
  );

  return (
    <div>
      <motion.div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`
          relative rounded-xl border-2 border-dashed p-12 text-center cursor-pointer
          transition-colors duration-150
          ${dragOver
            ? 'border-indigo-300 bg-indigo-500/5'
            : 'border-dark-border hover:border-indigo-500/50'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        whileHover={disabled ? undefined : { scale: 1.005 }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept=".pdf,.docx,.md,.markdown,.txt,.ipynb"
          onChange={(e) => {
            if (e.target.files) validateAndSelect(e.target.files);
            e.target.value = '';
          }}
          className="hidden"
        />

        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mx-auto mb-4">
          <Upload size={24} className="text-white" />
        </div>
        <p className="text-content font-body font-medium mb-1">
          {multiple ? 'Drag and drop one or more documents' : 'Drag and drop a document'}
        </p>
        <p className="text-sm text-content-muted font-body">
          PDF, Word, Markdown, Jupyter notebook, or text — up to 50MB each
        </p>
      </motion.div>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-error text-sm font-body mt-3 text-center"
        >
          {error}
        </motion.p>
      )}
    </div>
  );
}
